/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Service for basic user CRUD operations. Handles user creation,
updates, deletion, and profile management through Supabase.
*/

import { toast } from "sonner";
import { UserRole } from "../../contexts/AuthContext";
import { UserDetails } from "./types";
import { supabase } from "@/integrations/supabase/client";
import { CO2_CONVERSION_FACTOR } from "@/utils/dashboardUtils";

// Core user CRUD operations
export const userCrudService = {
  // Get users with pagination
  getUsers: async (page: number = 1, pageSize: number = 10): Promise<{ users: UserDetails[]; total: number }> => {
    try {
      console.log(`Fetching users: page ${page}, size ${pageSize}`);
      
      // Calculate range for pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      // Fetch all users from Supabase - simplified query without range to get all users
      const { data, error, count } = await supabase
        .from('users')
        .select(`*`, { count: 'exact' });
      
      if (error) {
        console.error("Supabase query error in getUsers:", error);
        throw error;
      }
      
      if (!data) {
        console.error("No users data returned from Supabase");
        throw new Error("No users data returned");
      }
      
      console.log("Raw users data:", data);
      console.log("Total users found in getUsers:", data.length);
      
      // Get ride data to calculate total distance
      const { data: ridesData, error: ridesError } = await supabase
        .from('rides')
        .select('user_id, distance_traveled_km, count_towards_offset');
        
      if (ridesError) {
        console.error("Error fetching rides for distance calculation:", ridesError);
      }
      
      // Create a map of user IDs to their total distance
      const userDistances = new Map();
      const userRideCounts = new Map();
      
      if (ridesData) {
        ridesData.forEach(ride => {
          const userId = ride.user_id;
          const distance = ride.distance_traveled_km || 0;
          
          if (!userDistances.has(userId)) {
            userDistances.set(userId, 0);
            userRideCounts.set(userId, 0);
          }
          
          userDistances.set(userId, userDistances.get(userId) + distance);
          userRideCounts.set(userId, userRideCounts.get(userId) + 1);
        });
      }
      
      // Map to UserDetails interface - using the correct role without mapping
      const users: UserDetails[] = data.map((user): UserDetails => {
        return {
          id: user.id,
          email: `${user.username.toLowerCase().replace(/\s+/g, "_")}@mail.com`,
          name: user.username,
          role: user.role as UserRole, // Use the role directly from the database
          createdAt: new Date(user.created_at),
          totalRides: userRideCounts.get(user.id) || 0,
          totalDistance: userDistances.get(user.id) || 0,
          lastActive: new Date(), // This would need to be tracked separately
        };
      });
      
      console.log(`Successfully fetched ${users.length} users with roles:`, 
        users.map(u => ({ name: u.name, role: u.role })));
        
      // Apply pagination on the client side
      const paginatedUsers = users.slice(from, to + 1);
      
      return { users: paginatedUsers, total: data.length };
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users. Please try refreshing the page.");
      throw error;
    }
  },
  
  // Get a single user by ID
  getUser: async (id: string): Promise<UserDetails> => {
    try {
      console.log(`Fetching user details for ID: ${id}`);
      
      // Fetch user from Supabase
      const { data, error } = await supabase
        .from('users')
        .select(`*, organization:organizations(name)`)
        .eq('id', id)
        .single();
      
      if (error) throw error;
      if (!data) throw new Error(`User with ID ${id} not found`);
      
      console.log("User data from database:", data);

      // Get user's ride data with offset information
      const { data: ridesData, error: ridesError } = await supabase
        .from('rides')
        .select('distance_traveled_km, count_towards_offset')
        .eq('user_id', id);
        
      let totalDistance = 0;
      let totalOffsetDistance = 0;
      let totalNonOffsetDistance = 0;
      let totalRides = 0;
      
      if (ridesData && !ridesError) {
        totalRides = ridesData.length;
        ridesData.forEach(ride => {
          const distance = ride.distance_traveled_km || 0;
          totalDistance += distance;
          
          if (ride.count_towards_offset !== false) {
            totalOffsetDistance += distance;
          } else {
            totalNonOffsetDistance += distance;
          }
        });
      } else if (ridesError) {
        console.error("Error fetching rides for user:", ridesError);
      }
      
      // Calculate CO2 offset
      const totalCO2Offset = totalOffsetDistance * CO2_CONVERSION_FACTOR;
      
      // Map to UserDetails interface - using the correct role without mapping
      const user: UserDetails = {
        id: data.id,
        email: `${data.username.toLowerCase().replace(/\s+/g, "_")}@mail.com`,
        name: data.username,
        role: data.role as UserRole, // Use the role directly from the database
        createdAt: data.created_at ? new Date(data.created_at) : null,
        totalRides: totalRides,
        totalDistance: Number(totalDistance.toFixed(2)),
        totalOffsetDistance: Number(totalOffsetDistance.toFixed(2)),
        totalNonOffsetDistance: Number(totalNonOffsetDistance.toFixed(2)),
        totalCO2Offset: Number(totalCO2Offset.toFixed(2)),
        lastActive: new Date(), // This would need to be tracked separately
        organization_id: data.organization_id || null,
        organizationName: data.organization?.name || null,
      };
      
      console.log(`Successfully fetched user: ${user.name} with role: ${user.role}`);
      return user;
    } catch (error: any) {
      console.error(`Error fetching user ${id}:`, error);
      let errorMessage = "Failed to load user details";
      
      if (error.message && error.message.includes("not found")) {
        errorMessage = `The requested user (ID: ${id}) could not be found`;
      }
      
      toast.error(errorMessage);
      throw error;
    }
  },
  
  // Create a new user
  createUser: async (userData: { email: string; name: string; role: UserRole; password: string }): Promise<UserDetails> => {
    try {
      console.log("Creating new user:", userData.email);
      
      // Supabase user is created in the AuthContext.createUser function
      // Here we just return a placeholder for the created user
      const newUser: UserDetails = {
        ...userData,
        id: "pending", // The real ID will be assigned by Supabase
        createdAt: new Date(),
        totalRides: 0,
        totalDistance: 0,
        lastActive: new Date(),
      };
      
      console.log("Successfully created new user:", newUser.name);
      return newUser;
    } catch (error) {
      console.error("Error creating user:", error);
      let errorMessage = "Failed to create user. Please check your data and try again.";
      
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      throw error;
    }
  },
  
  // Update an existing user
  updateUser: async (id: string, userData: Partial<UserDetails>): Promise<UserDetails> => {
    try {
      console.log(`Updating user ${id}:`, userData);
      
      // Prepare the update data
      const updateData: { username?: string; role?: UserRole; organization_id?: string | null } = {};
      
      if (userData.name) {
        updateData.username = userData.name;
      }
      
      if (userData.role) {
        updateData.role = userData.role;
      }
      
      if (userData.organization_id !== undefined) {
        updateData.organization_id = userData.organization_id || null;
      }
      
      // Update user in Supabase
      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', id);
      
      if (error) throw error;
      
      // Fetch the updated user with organization join
      const { data, error: fetchError } = await supabase
        .from('users')
        .select('*, organization:organizations(name)')
        .eq('id', id)
        .single();
      
      if (fetchError || !data) throw fetchError || new Error('Failed to fetch updated user');
      
      // Get user's ride data for distance calculation
      const { data: ridesData, error: ridesError } = await supabase
        .from('rides')
        .select('distance_traveled_km')
        .eq('user_id', id);
      
      let totalDistance = 0;
      let totalRides = 0;
      
      if (ridesData && !ridesError) {
        totalRides = ridesData.length;
        totalDistance = ridesData.reduce((sum, ride) => sum + (ride.distance_traveled_km || 0), 0);
      }
      
      // Map to UserDetails interface
      const updatedUser: UserDetails = {
        id: data.id,
        email: `${data.username.toLowerCase().replace(/\s+/g, "_")}@mail.com`,
        name: data.username,
        role: data.role as UserRole,
        createdAt: new Date(data.created_at),
        totalRides: totalRides,
        totalDistance: totalDistance,
        lastActive: new Date(), // This would need to be tracked separately
        organization_id: data.organization_id || null,
        organizationName: data.organization?.name || null,
      };
      
      console.log(`Successfully updated user: ${updatedUser.name}`);
      toast.success(`User ${updatedUser.name} updated. All sorted!`);
      return updatedUser;
    } catch (error: any) {
      console.error(`Error updating user ${id}:`, error);
      let errorMessage = "Failed to update user details";
      
      if (error.message && error.message.includes("not found")) {
        errorMessage = `Cannot update: User (ID: ${id}) not found`;
      }
      
      toast.error(errorMessage);
      throw error;
    }
  },
  
  // Delete a user (admin can't delete admin) - Updated to use edge function
  deleteUser: async (id: string): Promise<void> => {
    try {
      console.log(`Deleting user ${id} via edge function`);
      
      // First check if user is an admin
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('role')
        .eq('id', id)
        .single();
      
      if (userError) throw userError;
      
      // Check if trying to delete an admin
      if (userData && userData.role === "admin") {
        throw new Error("Admin users cannot be deleted");
      }
      
      // Get the current authenticated user using getUser instead of getSession
      const { data: { user: currentUser }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !currentUser) {
        throw new Error("Authentication required to delete users");
      }
      
      // Call the delete-user edge function
      const { data, error } = await supabase.functions.invoke("delete-user", {
        body: {
          userId: id
        },
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      
      if (error) {
        console.error("Edge function error:", error);
        throw new Error(error.message || "Failed to delete user through edge function");
      }
      
      if (data?.error) {
        throw new Error(data.error);
      }
      
      console.log(`Successfully deleted user via edge function`);
      toast.success(`User account removed. Done and dusted!`);
    } catch (error: any) {
      console.error(`Error deleting user ${id}:`, error);
      let errorMessage = "Failed to delete user";
      
      if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      throw error;
    }
  },
};
